"use client";

import { Add, Close, Delete, Favorite, IconError } from "@ellaandil/ui";
import { cva, type VariantProps } from "class-variance-authority";
import Image from "next/image";
import { forwardRef, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { Link } from "@/components/ui/Link";
import { Price } from "@/components/ui/Price";
import { Quantity } from "@/components/ui/Quantity";
import { Skeleton } from "@/components/ui/Skeleton";
import { useTranslations } from "@/i18n/client";

const cardVariants = cva("flex w-full relative", {
	variants: {
		type: {
			"shopping-cart": "gap-grid-component-spacing-md items-start",
			checkout: "gap-grid-component-spacing-md items-start",
			"addon-medium": "gap-grid-grid-gutter-md items-start",
			"addon-small": "gap-grid-grid-gutter-md items-center",
			gift: "gap-grid-component-spacing-md items-start border border-solid border-border-medium rounded-general-sm p-grid-component-spacing-md",
			bundle: "flex-col gap-grid-component-spacing-md items-start",
		},
	},
	defaultVariants: {
		type: "shopping-cart",
	},
});

const imageVariants = cva("relative shrink-0 bg-background-bg-image", {
	variants: {
		size: {
			large: "w-[117px] h-[156px]",
			medium: "w-[77px] h-[102px]",
			small: "w-[53px] h-[71px]",
		},
	},
	defaultVariants: {
		size: "large",
	},
});

type CartListingCardVariants = VariantProps<typeof cardVariants>;

type Attribute = { label: string; value: string };

type InlineAlertProps = {
	message: string;
	onDismiss?: () => void;
};

function InlineAlert({ message, onDismiss }: InlineAlertProps) {
	const t = useTranslations();
	return (
		<div className="flex gap-grid-component-spacing-2xs items-start w-full text-status-error-text">
			<p className="flex-1 font-body font-regular text-xs leading-[1.5]">{message}</p>
			{onDismiss && (
				<button
					type="button"
					aria-label={t("Dismiss")}
					onClick={onDismiss}
					className="inline-flex items-center justify-center size-5 shrink-0 rounded-component-specific-button bg-transparent border border-transparent cursor-pointer"
				>
					<Close className="size-3.5" />
				</button>
			)}
		</div>
	);
}

type CartListingCardProps = CartListingCardVariants & {
	image: { src: string; alt?: string };
	brand?: string;
	name: string;
	href?: string;
	attributes?: Attribute[];
	price?: string;
	originalPrice?: string;
	badge?: {
		label: string;
		color?: React.ComponentProps<typeof Badge>["color"];
	};
	stockLevelLabel?: string;
	quantity?: number;
	onQuantityDecrement?: () => void;
	onQuantityIncrement?: () => void;
	showFavoritesButton?: boolean;
	showDeleteButton?: boolean;
	onFavorite?: () => void;
	onRemove?: () => void;
	addButtonLabel?: string;
	onAdd?: () => void;
	alertMessage?: string;
	bundleItems?: ReactNode;
	bundleTitle?: string;
	/**
	 * Override the brand-row visibility. By default the brand renders for
	 * shopping-cart and checkout types when a `brand` value is provided. Set to
	 * `false` to hide it everywhere.
	 */
	showBrand?: boolean;
	className?: string;
};

const imageSizeFor = (type: CartListingCardVariants["type"]) => {
	if (type === "addon-small") return "small" as const;
	if (type === "addon-medium" || type === "gift") return "medium" as const;
	return "large" as const;
};

export const CartListingCard = forwardRef<HTMLDivElement, CartListingCardProps>(
	(
		{
			type = "shopping-cart",
			image,
			brand,
			name,
			href,
			attributes,
			price,
			originalPrice,
			badge,
			stockLevelLabel,
			quantity,
			onQuantityDecrement,
			onQuantityIncrement,
			showFavoritesButton,
			showDeleteButton,
			onFavorite,
			onRemove,
			addButtonLabel,
			onAdd,
			alertMessage,
			bundleItems,
			bundleTitle = "Goes together with",
			showBrand: showBrandProp,
			className,
		},
		ref,
	) => {
		const t = useTranslations();
		const isCheckout = type === "checkout";
		const isAddonSmall = type === "addon-small";
		const isBundle = type === "bundle";
		const showBrand = showBrandProp ?? Boolean(brand && (type === "shopping-cart" || isCheckout));
		const imageSize = imageSizeFor(type);
		const showAttributes = attributes && attributes.length > 0 && !isAddonSmall;
		const showStockLevel = stockLevelLabel && (type === "shopping-cart" || isCheckout);
		const showAlert = alertMessage && (type === "shopping-cart" || isCheckout);
		const showQuantityStepper = typeof quantity === "number" && !isCheckout && !isBundle;
		const showInlineQuantity = isAddonSmall && showQuantityStepper;
		const showStackedQuantity = !isAddonSmall && showQuantityStepper;

		const nameNode = (
			<span className="font-display font-light leading-[1.25] text-text-dark-text-1 text-xs w-full">
				{name}
			</span>
		);

		const priceNode = price ? <Price price={price} originalPrice={originalPrice} /> : null;

		const imageNode = (
			<div className={imageVariants({ size: imageSize })}>
				<Image
					src={image.src}
					alt={image.alt ?? name}
					fill
					sizes="135px"
					className="object-cover"
				/>
				{isCheckout && typeof quantity === "number" && (
					<output
						aria-label={`Quantity ${quantity}`}
						className="absolute -top-2 -right-2 inline-flex items-center justify-center size-[18px] rounded-full bg-neutral-black-95 text-neutral-white-100 font-body font-regular text-2xs leading-none tabular-nums lining-nums text-box-cap"
					>
						{quantity}
					</output>
				)}
			</div>
		);

		const detailsNode = (
			<div className="flex flex-col flex-1 min-w-0 gap-3 items-start self-stretch">
				<div className="flex gap-grid-component-spacing-2xs items-start w-full">
					<div className="flex flex-col flex-1 min-w-0 gap-[2px] items-start">
						{showBrand && (
							<p className="font-body font-regular text-xs leading-[1.5] text-text-dark-text-2 w-full">
								{brand}
							</p>
						)}
						{href ? <a href={href}>{nameNode}</a> : nameNode}
						{isAddonSmall && priceNode}
					</div>
					{showFavoritesButton && (
						<IconButton
							icon={Favorite}
							label={t("Save to favorites")}
							variant="ghost-primary"
							size="sm"
							onClick={onFavorite}
						/>
					)}
					{showDeleteButton && (
						<IconButton
							icon={Delete}
							label={t("Remove")}
							variant="ghost-primary"
							size="sm"
							onClick={onRemove}
						/>
					)}
				</div>

				{(showAttributes || (!isAddonSmall && (priceNode || badge)) || showStockLevel) && (
					<div className="flex flex-col gap-grid-component-spacing-2xs items-start justify-end w-full">
						{showAttributes && (
							<dl className="flex flex-col gap-grid-component-spacing-4xs items-start w-full font-body font-regular text-xs leading-[1.15] text-text-dark-text-1 uppercase tabular-nums lining-nums whitespace-nowrap">
								{attributes?.map((attr) => (
									<div
										key={attr.label}
										className="flex gap-grid-component-spacing-4xs items-end w-full"
									>
										<dt>{attr.label}:</dt>
										<dd>{attr.value}</dd>
									</div>
								))}
							</dl>
						)}

						{!isAddonSmall && (priceNode || badge || showStockLevel) && (
							<div className="flex flex-col gap-[2px] items-start w-full">
								{(priceNode || badge) && (
									<div className="flex gap-grid-component-spacing-2xs items-center w-full">
										{priceNode}
										{badge && (
											<Badge color={badge.color ?? "sale"} size="small">
												{badge.label}
											</Badge>
										)}
									</div>
								)}
								{showStockLevel && (
									<Link variant="secondary" size="small" leftIcon={IconError} href="#">
										{stockLevelLabel}
									</Link>
								)}
							</div>
						)}
					</div>
				)}

				{showStackedQuantity && (
					<Quantity
						value={quantity as number}
						onDecrement={onQuantityDecrement}
						onIncrement={onQuantityIncrement}
					/>
				)}

				{showAlert && <InlineAlert message={alertMessage as string} />}
			</div>
		);

		const mainRow = (
			<>
				{imageNode}
				{detailsNode}
				{showInlineQuantity && (
					<Quantity
						value={quantity as number}
						onDecrement={onQuantityDecrement}
						onIncrement={onQuantityIncrement}
					/>
				)}
				{isAddonSmall && addButtonLabel && (
					<Button variant="outline" color="primary" size="small" leftIcon={Add} onClick={onAdd}>
						{addButtonLabel}
					</Button>
				)}
			</>
		);

		if (isBundle) {
			return (
				<div ref={ref} className={twMerge(cardVariants({ type }), className)}>
					<div className="flex gap-grid-component-spacing-md items-start w-full">
						{imageNode}
						{detailsNode}
					</div>
					{bundleItems && (
						<div className="flex flex-col gap-grid-component-spacing-md items-start w-full">
							<p className="font-display font-light text-base leading-[1.25] text-text-dark-text-1">
								{bundleTitle}
							</p>
							<div className="flex flex-col gap-grid-component-spacing-md items-start w-full">
								{bundleItems}
							</div>
						</div>
					)}
				</div>
			);
		}

		return (
			<div ref={ref} className={twMerge(cardVariants({ type }), className)}>
				{mainRow}
			</div>
		);
	},
);

CartListingCard.displayName = "CartListingCard";

export const CartListingCardSkeleton = ({
	type = "checkout",
	className,
}: {
	type?: CartListingCardVariants["type"];
	className?: string;
}) => {
	const imageSize = imageSizeFor(type);

	return (
		<div className={twMerge(cardVariants({ type }), className)}>
			<div className={imageVariants({ size: imageSize })}>
				<Skeleton className="w-full h-full" />
			</div>
			<div className="flex flex-col flex-1 min-w-0 gap-3 items-start self-stretch">
				<div className="flex gap-grid-component-spacing-2xs items-start w-full">
					<div className="flex flex-col flex-1 min-w-0 gap-[2px] items-start">
						<Skeleton fitContent>
							<p className="font-body font-regular text-xs leading-[1.5] text-text-dark-text-2">
								Brand Name
							</p>
						</Skeleton>
						<Skeleton fitContent>
							<span className="font-display font-light leading-[1.25] text-text-dark-text-1 text-xs">
								Product Name
							</span>
						</Skeleton>
					</div>
				</div>
				<div className="flex flex-col gap-grid-component-spacing-2xs items-start justify-end w-full">
					<Skeleton fitContent>
						<dl className="flex flex-col gap-grid-component-spacing-2xs items-start w-full font-body font-regular text-xs leading-[1.15] text-text-dark-text-1 uppercase tabular-nums lining-nums whitespace-nowrap">
							<div className="flex gap-grid-component-spacing-4xs items-end w-full">
								<dt>Color:</dt>
								<dd>Black</dd>
							</div>
							<div className="flex gap-grid-component-spacing-4xs items-end w-full">
								<dt>Size:</dt>
								<dd>M</dd>
							</div>
						</dl>
					</Skeleton>
					<Skeleton fitContent>
						<div className="flex gap-grid-component-spacing-2xs items-center w-full">
							<Price price="1000" />
						</div>
					</Skeleton>
				</div>
			</div>
		</div>
	);
};
